// Shared contracts for clients, projects, tasks and tags. Owned by the catalog feature;
// consumed by time tracking, reports and import.
import { z } from 'zod';

/** Clockify's project colour palette (first is the default). */
export const PROJECT_COLORS = [
  '#03A9F4',
  '#2196F3',
  '#3F51B5',
  '#673AB7',
  '#9C27B0',
  '#E91E63',
  '#F44336',
  '#FF5722',
  '#FF9800',
  '#FFC107',
  '#CDDC39',
  '#8BC34A',
  '#4CAF50',
  '#009688',
  '#00BCD4',
  '#607D8B',
  '#795548',
  '#9E9E9E',
] as const;

const name = z.string().trim().min(1, 'Name is required').max(250);

export const clientSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  note: z.string().nullable(),
  archived: z.boolean(),
  /** Entries on this client's projects can't be saved without a description (Clockify's required fields). */
  requireDescription: z.boolean(),
  /** Entries on this client's projects can't be saved without at least one tag. */
  requireTags: z.boolean(),
});
export type Client = z.infer<typeof clientSchema>;

export const clientInputSchema = z.object({
  name,
  note: z.string().max(3000).nullish().default(null),
  requireDescription: z.boolean().default(true),
  requireTags: z.boolean().default(true),
});
export type ClientInput = z.input<typeof clientInputSchema>;

export const taskSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string(),
  done: z.boolean(),
});
export type Task = z.infer<typeof taskSchema>;

export const taskInputSchema = z.object({ name, done: z.boolean().optional().default(false) });
export type TaskInput = z.input<typeof taskInputSchema>;

export const projectMemberSchema = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string(),
  isManager: z.boolean(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

/**
 * GET /api/projects returns Project[] (with tasks) visible to the caller:
 * public projects plus private ones they are a member of (admins/managers see all).
 * Query: ?archived=true|false (default false) &search=text &clientId=uuid
 */
export const projectSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string(),
  clientId: z.uuid().nullable(),
  clientName: z.string().nullable(),
  billableDefault: z.boolean(),
  isPublic: z.boolean(),
  archived: z.boolean(),
  note: z.string().nullable(),
  /** The client's required fields (false without a client). */
  requireDescription: z.boolean(),
  requireTags: z.boolean(),
  tasks: z.array(taskSchema),
});
export type Project = z.infer<typeof projectSchema>;

/** Creates a client with this name together with the project (needs catalog:manage). */
const newClientName = name.optional();
const oneClientChoice = {
  message: 'Choose an existing client or a new one, not both',
  path: ['newClientName'],
};

export const projectInputSchema = z
  .object({
    name,
    clientId: z.uuid().nullish().default(null),
    newClientName,
    color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Colour must be a hex value like #03A9F4')
      .default(PROJECT_COLORS[0]),
    billableDefault: z.boolean().default(true),
    isPublic: z.boolean().default(true),
    note: z.string().max(3000).nullish().default(null),
  })
  .refine((v) => !(v.clientId && v.newClientName), oneClientChoice);
export type ProjectInput = z.input<typeof projectInputSchema>;

/**
 * Tags belong to one client (or to no client). An entry may only carry tags of its project's
 * client; entries without a client only tags without one. Names are unique per client.
 */
export const tagSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  archived: z.boolean(),
  clientId: z.uuid().nullable(),
  clientName: z.string().nullable(),
});
export type TagItem = z.infer<typeof tagSchema>;

/** Longest tag name. Clockify allows 100; Taim allows more, as teams use tags for ticket titles. */
export const MAX_TAG_NAME = 255;
const tagName = z.string().trim().min(1, 'Name is required').max(MAX_TAG_NAME);

export const tagInputSchema = z.object({ name: tagName, clientId: z.uuid().nullable().default(null) });
export type TagInput = z.input<typeof tagInputSchema>;

// Partial updates. Omitted fields are left unchanged; `null` clears a nullable field.

const color = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Colour must be a hex value like #03A9F4');
const note = z.string().max(3000).nullish();

export const clientPatchSchema = z.object({
  name: name.optional(),
  note,
  archived: z.boolean().optional(),
  requireDescription: z.boolean().optional(),
  requireTags: z.boolean().optional(),
});
export type ClientPatch = z.input<typeof clientPatchSchema>;

export const projectPatchSchema = z
  .object({
    name: name.optional(),
    clientId: z.uuid().nullish(),
    newClientName,
    color: color.optional(),
    billableDefault: z.boolean().optional(),
    isPublic: z.boolean().optional(),
    note,
    archived: z.boolean().optional(),
  })
  .refine((v) => !(v.clientId && v.newClientName), oneClientChoice);
export type ProjectPatch = z.input<typeof projectPatchSchema>;

export const taskPatchSchema = z.object({ name: name.optional(), done: z.boolean().optional() });
export type TaskPatch = z.input<typeof taskPatchSchema>;

/** Moving a tag to another client is refused while time entries use it. */
export const tagPatchSchema = z.object({
  name: tagName.optional(),
  archived: z.boolean().optional(),
  clientId: z.uuid().nullable().optional(),
});
export type TagPatch = z.input<typeof tagPatchSchema>;

/** PUT /api/projects/:id/members replaces the project's member list. */
export const projectMembersInputSchema = z.object({
  members: z
    .array(z.object({ userId: z.uuid(), isManager: z.boolean().default(false) }))
    .max(5000)
    .refine((list) => new Set(list.map((m) => m.userId)).size === list.length, {
      message: 'Each user can only be listed once',
    }),
});
export type ProjectMembersInput = z.input<typeof projectMembersInputSchema>;

/**
 * Query for GET /api/clients, /api/tags and /api/projects.
 * archived: 'false' (default, active only), 'true' (archived only) or 'all'.
 */
export const ARCHIVED_FILTERS = ['false', 'true', 'all'] as const;
export type ArchivedFilter = (typeof ARCHIVED_FILTERS)[number];
export const catalogListQuerySchema = z.object({
  archived: z.enum(ARCHIVED_FILTERS).default('false'),
  search: z.string().trim().max(250).optional(),
});
export const projectListQuerySchema = catalogListQuerySchema.extend({
  clientId: z.uuid().optional(),
  /**
   * Admins and managers: the projects this user may log time to (what they see), e.g. to edit
   * their entries. Others may only name themselves.
   */
  userId: z.uuid().optional(),
});
export type ProjectListQuery = z.input<typeof projectListQuerySchema>;

/**
 * GET /api/tags. clientId: a client's tags, or 'none' for tags without a client; omitted = all
 * tags (reports, the Tags page).
 */
export const tagListQuerySchema = catalogListQuerySchema.extend({
  clientId: z.union([z.uuid(), z.literal('none')]).optional(),
});
export type TagListQuery = z.input<typeof tagListQuerySchema>;
